/**
 * PDP Master JavaScript
 * Handles all interactive functionality for the PDP master template
 */

class PDPMaster {
  constructor() {
    this.init();
  }

  init() {
    this.setUpZoom();
    this.setupHeaderHeight();
    this.setupVariantHandling();
    this.setupDispatchMessaging();
    this.setupDescriptionToggle();
    this.setupStickyBehavior();
    this.setupMediaSync();
    this.setupAccordions();
    this.setupDesktopGalleryPagination();
    this.setupMobileGalleryPagination();
    this.bindEvents();
  }

  setUpZoom() {
      const containers = document.querySelectorAll('.pdp-media-item');
      
      containers.forEach(container => {
        const image = container.querySelector('img');
        
        if (image) {
          container.addEventListener('mousemove', (e) => {
            const rect = container.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            
            image.style.transformOrigin = `${x}% ${y}%`;
            image.style.transform = 'scale(2)';
          });

          container.addEventListener('mouseleave', () => {
            image.style.transform = 'scale(1)';
          });
        }
      });
  }

  setupHeaderHeight() {
    // Get header height and set CSS variable
    const header = document.querySelector('.section-header');
    const stickyHeader = document.querySelector('sticky-header');
    if (header) {
      const setHeight = () => {
        const headerHeight = header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);

        // Sticky panels only make room for a header that stays on screen on desktop
        const isHeaderAlwaysVisible = stickyHeader?.dataset.stickyTypeDesktop === 'always';
        document.documentElement.style.setProperty('--pdp-sticky-offset', `${isHeaderAlwaysVisible ? headerHeight : 0}px`);
      };
      
      setHeight();
      
      // Update on resize
      window.addEventListener('resize', () => {
        clearTimeout(this.headerResizeTimeout);
        this.headerResizeTimeout = setTimeout(setHeight, 100);
      });
    }
  }

  setupVariantHandling() {
    // Listen for variant changes from any variant selector
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail.variant);
    });

    // Subscribe to pub-sub variant change event (for palette picker and other components)
    subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
      if (data.data && data.data.variant) {
        this.handleVariantChange(data.data.variant);
      }
    });

    // Setup standard variant picker if present
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      this.setupStandardVariantPicker(variantSelects);
    }

    // Setup metal/stone variant picker if present
    const metalStonePicker = document.querySelector('.pdp-variant-metal-stone');
    if (metalStonePicker) {
      this.setupMetalStonePicker(metalStonePicker);
    }

    // Setup size dropdown if present
    const sizeDropdown = document.querySelector('.size-select');
    if (sizeDropdown) {
      this.setupSizeDropdown(sizeDropdown);
    }

    // Capture initial variant from DOM for dispatch messaging
    this.currentVariantId = this.getCurrentVariantId();
  }

  setupDispatchMessaging() {
    this.dispatchTargets = Array.from(document.querySelectorAll('[data-dispatch-wrapper]')).map(
      (wrapper) => {
        const sectionId = wrapper.getAttribute('data-dispatch-section');
        const inStockEl = wrapper.querySelector('[data-dispatch-state="in-stock"]');
        const outOfStockEl = wrapper.querySelector('[data-dispatch-state="out-of-stock"]');
        const mapScript = sectionId
          ? document.getElementById(`dispatch-variant-data-${sectionId}`)
          : null;
        let variantMap = {};
        if (mapScript) {
          try {
            variantMap = JSON.parse(mapScript.textContent || '{}');
          } catch (error) {
            console.warn('Dispatch message: failed to parse map for section', sectionId, error);
          }
        }
        return { sectionId, wrapper, inStockEl, outOfStockEl, variantMap };
      }
    );

    const initialVariantId = this.getCurrentVariantId();
    if (initialVariantId) {
      this.updateDispatchMessage({ id: initialVariantId });
    }
  }

  getCurrentVariantId() {
    const variantInput = document.querySelector('.product-form input.product-variant-id');
    if (variantInput && variantInput.value) {
      const parsed = Number(variantInput.value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  setupStandardVariantPicker(variantSelects) {
    const form = variantSelects.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    // Listen for change events on variant-selects (catches bubbled events from hidden selects)
    const queueVariantUpdate = () => {
      requestAnimationFrame(() => {
        this.updateSelectedVariant(variantSelects, form);
      });
    };

    variantSelects.addEventListener('change', queueVariantUpdate);

    // Also listen for direct changes on hidden selects (custom dropdowns)
    const hiddenSelects = variantSelects.querySelectorAll('select.custom-dropdown__hidden-select');
    hiddenSelects.forEach(select => {
      select.addEventListener('change', queueVariantUpdate);
    });
  }

  updateVariantFromScript(variantSelects) {
    // Extract variant from data-selected-variant script tag
    const variantScript = variantSelects.querySelector('[data-selected-variant]');
    if (!variantScript) {
      console.warn('No data-selected-variant script found');
      return;
    }

    try {
      const variant = JSON.parse(variantScript.textContent);
      if (variant && variant.id) {
        // Add a small delay to allow product-info.js to update the HTML first
        setTimeout(() => {
          this.handleVariantChange(variant);
        }, 150);
      } else {
        console.warn('Variant data is missing or invalid:', variant);
      }
    } catch (e) {
      console.error('Error parsing variant data:', e);
    }
  }

  updateSelectedVariant(variantSelects, form) {
    const selectedOptions = [];

    // Get all selected options
    const radioInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
    const selectInputs = variantSelects.querySelectorAll('select');

    radioInputs.forEach(input => {
      selectedOptions.push(input.value);
    });

    selectInputs.forEach(select => {
      if (select.value) {
        selectedOptions.push(select.value);
      }
    });

    // Find matching variant
    const productData = document.getElementById('product-json-' + variantSelects.dataset.section);
    if (productData) {
      const variants = JSON.parse(productData.textContent);
      const matchingVariant = variants.find(variant => {
        return selectedOptions.every(option => variant.options.includes(option));
      });

      if (matchingVariant) {
        this.handleVariantChange(matchingVariant);

        // Update form variant ID
        const variantIdInput = form.querySelector('input[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = matchingVariant.id;
        }
      } else {
        this.updateVariantFromScript(variantSelects);
      }
    }
  }

  setupMetalStonePicker(metalStonePicker) {
    const metalSelect = metalStonePicker.querySelector('.metal-select');
    const stoneSelect = metalStonePicker.querySelector('.stone-select');
    
    if (metalSelect && stoneSelect) {
      // Metal selection will be handled by the snippet's own JavaScript
      // We just need to listen for variant changes
      metalStonePicker.addEventListener('variant:change', (event) => {
        this.handleVariantChange(event.detail.variant);
      });
    }
  }

  setupSizeDropdown(sizeDropdown) {
    // Size dropdown is for line-item properties, not variant selection
    // No special handling needed as it's just a form field
    sizeDropdown.addEventListener('change', () => {
      // Could add validation or other logic here if needed
    });
  }

  handleVariantChange(variant) {
    if (!variant) {
      console.warn('handleVariantChange called with null/undefined variant');
      return;
    }

    if (variant.id) {
      this.currentVariantId = Number(variant.id);
    }

    // Update price
    this.updatePrice(variant);

    // Update variant title
    this.updateVariantTitle(variant);

    // Update product images
    this.updateProductImages(variant);

    // Update availability
    this.updateAvailability(variant);

    // Update delivery messaging
    this.updateDispatchMessage(variant);

    // Update URL
    this.updateURL(variant);
  }

  // Format price with symbol only (removes currency code prefixes like "US$")
  formatPriceWithSymbol(price) {
    const activeCurrency =
      (window.Shopify?.currency?.active) ||
      (window.Shopify?.currency?.default) ||
      'GBP';

    let formatted;
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      formatted = Shopify.formatMoney(price, window.theme?.moneyFormat);
    } else {
      formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(price / 100);
    }

    // Remove currency code prefixes (e.g., "US$" → "$", "CA£" → "£")
    formatted = formatted.replace(/^[A-Z]{2,3}([$£€])/, '$1');

    return formatted;
  }

  updatePrice(variant) {
    if (!variant) {
      console.warn('updatePrice called with null/undefined variant');
      return;
    }
    
    if (variant.price === undefined) {
      console.warn('Variant price is undefined:', variant);
      return;
    }

    console.log('Updating price for variant:', variant.id, 'Price:', variant.price);

    // Find the price wrapper element (rendered by price.liquid snippet)
    const priceWrapper = document.querySelector('.pdp-master-en__price [id^="price-"]');
    if (!priceWrapper) {
      console.warn('Price wrapper not found: .pdp-master-en__price [id^="price-"]');
      return;
    }

    const priceContainer = priceWrapper.querySelector('.price');
    if (!priceContainer) {
      console.warn('Price container not found: .price');
      return;
    }

    // Determine if product is on sale by checking variant compare_at_price
    const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    // Find the price elements
    const priceRegular = priceContainer.querySelector('.price__regular');
    const priceSale = priceContainer.querySelector('.price__sale');

    // Format the price
    let formattedPrice = this.formatPriceWithSymbol(variant.price);

    // Update regular price
    if (priceRegular) {
      const regularPriceElement = priceRegular.querySelector('.price-item--regular');
      if (regularPriceElement) {
        // The price.liquid snippet wraps price in <span class="price__amount">
        regularPriceElement.innerHTML = `<span class="price__amount">${formattedPrice}</span>`;
      }
    }

    // Update sale price
    if (priceSale) {
      const salePriceElement = priceSale.querySelector('.price-item--sale');
      if (salePriceElement) {
        // The price.liquid snippet wraps price in <span class="price__amount">
        salePriceElement.innerHTML = `<span class="price__amount">${formattedPrice}</span>`;
      }

      // If on sale, also update the compare_at_price
      if (isOnSale) {
        let formattedComparePrice = this.formatPriceWithSymbol(variant.compare_at_price);
        
        // Find or create the compare_at_price element
        let compareAtPriceElement = priceSale.querySelector('.price-item--regular');
        if (!compareAtPriceElement) {
          // Create the compare_at_price element if it doesn't exist
          const saleContainer = priceSale.querySelector('.price__container');
          if (saleContainer) {
            const regularSpan = document.createElement('span');
            regularSpan.className = 'visually-hidden visually-hidden--inline';
            regularSpan.textContent = 'Regular price';
            compareAtPriceElement = document.createElement('span');
            compareAtPriceElement.className = 'price-item price-item--regular';
            saleContainer.insertBefore(regularSpan, salePriceElement);
            saleContainer.insertBefore(compareAtPriceElement, salePriceElement);
          }
        }
        
        if (compareAtPriceElement) {
          compareAtPriceElement.innerHTML = `<s class="price-item price-item--regular">${formattedComparePrice}</s>`;
        }
      }
    }

    // Update price container classes based on sale status
    priceContainer.classList.toggle('price--on-sale', isOnSale);
    priceContainer.classList.toggle('price--sold-out', !variant.available);

    // Show/hide price sections based on sale status
    if (priceRegular) {
      priceRegular.style.display = isOnSale ? 'none' : '';
    }
    if (priceSale) {
      priceSale.style.display = isOnSale ? '' : 'none';
    }
  }

  formatPrice(price, comparePrice) {
    const formattedPrice = this.formatPriceWithSymbol(price);
    let priceHTML = `<span class="price">${formattedPrice}</span>`;

    if (comparePrice && comparePrice > price) {
      const formattedComparePrice = this.formatPriceWithSymbol(comparePrice);

      priceHTML = `
        <span class="price price--on-sale">
          <span class="price-item price-item--sale">${formattedPrice}</span>
          <span class="price-item price-item--regular">${formattedComparePrice}</span>
        </span>
      `;
    }

    return priceHTML;
  }

  updateVariantTitle(variant) {
    // Update only the main product title span, preserve stacked products span
    const mainTitleElements = document.querySelectorAll('#main-product-title');

    mainTitleElements.forEach(element => {
      if (variant.title) {
        const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
        element.dataset.variantTitle = variantTitle;
        element.textContent = variantTitle ? `'${variantTitle}'` : '';
      }
    });

    // Notify stack drawer to update its display if in pre_select mode
    if (typeof window.updateStackProductDisplay === 'function') {
      window.updateStackProductDisplay();
    }
  }

  updateProductImages(variant) {
    if (!variant.featured_image) return;

    // Update main product images to show variant image first
    const mediaItems = document.querySelectorAll('.pdp-media-item, .mobile-gallery-slide');

    // Find the variant image in the media
    mediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (img && img.src.includes(variant.featured_image.id)) {
        // Scroll this image into view
        this.scrollToMediaItem(index);
      }
    });
  }

  updateDispatchMessage(variant) {
    if (!this.dispatchTargets || !this.dispatchTargets.length || !variant) return;

    const variantIdFromDom = this.getCurrentVariantId();
    const variantId = variantIdFromDom || Number(variant.id || variant);

    this.dispatchTargets.forEach(({ inStockEl, outOfStockEl, variantMap }) => {
      const entry = variantMap ? variantMap[variantId] : null;

      if (inStockEl) {
        inStockEl.hidden = true;
        inStockEl.setAttribute('aria-hidden', 'true');
        inStockEl.style.display = 'none';
      }

      if (outOfStockEl) {
        outOfStockEl.hidden = true;
        outOfStockEl.setAttribute('aria-hidden', 'true');
        outOfStockEl.style.display = 'none';
      }

      const available =
        entry && entry.available !== undefined
          ? entry.available
          : variant.available !== undefined
          ? variant.available
          : false;
      const inventoryManagement =
        entry && entry.inventory_management !== undefined
          ? entry.inventory_management
          : variant.inventory_management;
      const inventoryQuantity =
        entry && entry.inventory_quantity !== undefined && entry.inventory_quantity !== null
          ? entry.inventory_quantity
          : variant.inventory_quantity;

      let isInStock = Boolean(available);
      if (inventoryManagement) {
        if (typeof inventoryQuantity === 'number' && !Number.isNaN(inventoryQuantity)) {
          isInStock = inventoryQuantity > 0;
        }
      }

      if (inStockEl) {
        inStockEl.hidden = !isInStock;
        inStockEl.setAttribute('aria-hidden', (!isInStock).toString());
        inStockEl.style.display = isInStock ? '' : 'none';
      }

      if (outOfStockEl) {
        const shouldShowOut = !isInStock;
        outOfStockEl.hidden = !shouldShowOut;
        outOfStockEl.setAttribute('aria-hidden', (!shouldShowOut).toString());
        outOfStockEl.style.display = shouldShowOut ? '' : 'none';
      }
    });
  }

  scrollToMediaItem(index) {
    // Desktop - scroll in media stack
    const mediaStack = document.getElementById('pdp-media-stack');
    if (mediaStack) {
      const targetItem = mediaStack.querySelector(`[data-media-index="${index}"]`);
      if (targetItem) {
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // Mobile - scroll in gallery
    const mobileGallery = document.getElementById('mobile-gallery');
    if (mobileGallery) {
      const slideWidth = mobileGallery.clientWidth;
      mobileGallery.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }

    // Update pagination dots
    this.updatePaginationDots(index);
  }

  updatePaginationDots(activeIndex) {
    // Update desktop pagination dots
    const desktopDots = document.querySelectorAll('#pdp-pagination .pagination-dot');
    desktopDots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });

    // Update mobile pagination dots
    const mobileDots = document.querySelectorAll('.mobile-gallery-progress .pagination-dot');
    mobileDots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }

  updateAvailability(variant) {
    const addToCartButtons = document.querySelectorAll(
      '.product-form__cart-submit, .mobile-add-to-cart'
    );

    addToCartButtons.forEach(button => {
      const span = button.querySelector('span');

      if (variant.available) {
        button.disabled = false;
        button.classList.remove('disabled');
        if (span) span.textContent = 'Add to Cart';
      } else {
        button.disabled = true;
        button.classList.add('disabled');
        if (span) span.textContent = 'Sold Out';
      }
    });
  }

  updateURL(variant) {
    if (!window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(null, '', url.toString());
  }

  setupDescriptionToggle() {
    // Desktop description toggle
    const descToggle = document.querySelector('.description-toggle');
    const descContent = document.querySelector('.description-content');

    if (descToggle && descContent) {
      descToggle.addEventListener('click', () => {
        const isExpanded = descToggle.getAttribute('aria-expanded') === 'true';

        descToggle.setAttribute('aria-expanded', !isExpanded);
        descContent.classList.toggle('expanded');

        const showMore = descToggle.querySelector('.show-more');
        const showLess = descToggle.querySelector('.show-less');

        if (isExpanded) {
          showMore.style.display = '';
          showLess.style.display = 'none';
        } else {
          showMore.style.display = 'none';
          showLess.style.display = '';
        }
      });
    }
  }

  setupStickyBehavior() {
    // Enhanced sticky behavior for media column
    const mediaColumn = document.querySelector('.pdp-master__column--media');
    const mediaSticky = document.querySelector('.pdp-master__media-sticky');

    if (!mediaColumn || !mediaSticky) return;

    let isSticky = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isSticky) {
          isSticky = true;
          mediaSticky.classList.add('is-sticky');
        } else if (!entry.isIntersecting && isSticky) {
          isSticky = false;
          mediaSticky.classList.remove('is-sticky');
        }
      });
    }, {
      root: null,
      threshold: 0,
      rootMargin: '-100px 0px 0px 0px'
    });

    observer.observe(mediaColumn);
  }

  setupMediaSync() {
    // This is already handled in the pdp-media-stack snippet
    // but we can add additional coordination here if needed
  }

  setupDesktopGalleryPagination() {
    const desktopGallery = document.getElementById('pdp-media-gallery');
    const desktopPagination = document.getElementById('pdp-pagination');
    
    if (!desktopGallery || !desktopPagination) return;

    const desktopDots = desktopPagination.querySelectorAll('.pagination-dot');
    const mediaItems = desktopGallery.querySelectorAll('.pdp-media-item');

    if (!desktopDots.length || !mediaItems.length) return;

    // Handle desktop pagination dot clicks
    desktopDots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const targetItem = mediaItems[index];
        if (targetItem) {
          targetItem.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      });
    });

    // Track which image is in view using IntersectionObserver
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const mediaIndex = entry.target.dataset.mediaIndex;
          if (mediaIndex !== undefined) {
            const index = parseInt(mediaIndex);
            desktopDots.forEach((dot, i) => {
              dot.classList.toggle('active', i === index);
            });
          }
        }
      });
    }, observerOptions);

    // Observe all media items (they already have data-media-index from the snippet)
    mediaItems.forEach(item => {
      observer.observe(item);
    });
  }

  setupMobileGalleryPagination() {
    const mobileGallery = document.getElementById('mobile-gallery');
    const mobilePagination = document.querySelector('.mobile-gallery-progress');
    
    if (!mobileGallery || !mobilePagination) return;

    const mobileDots = mobilePagination.querySelectorAll('.pagination-dot');

    // Handle mobile pagination dot clicks
    mobileDots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const slideWidth = mobileGallery.clientWidth;
        mobileGallery.scrollTo({
          left: slideWidth * index,
          behavior: 'smooth'
        });
      });
    });

    // Update active dot on scroll
    mobileGallery.addEventListener('scroll', () => {
      const slideWidth = mobileGallery.clientWidth;
      const currentSlideIndex = Math.round(mobileGallery.scrollLeft / slideWidth);
      
      mobileDots.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentSlideIndex);
      });
    });
  }

  setupAccordions() {
    // Enhanced accordion behavior
    const accordions = document.querySelectorAll('details.accordion, details.mobile-accordion');

    accordions.forEach(accordion => {
      const summary = accordion.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', () => {
          // Add smooth animation class
          accordion.classList.add('accordion-animating');

          setTimeout(() => {
            accordion.classList.remove('accordion-animating');
          }, 300);
        });
      }
    });
  }

  bindEvents() {
    // Global keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      // ESC key - close any open drawers
      if (e.key === 'Escape') {
        this.closeAllDrawers();
      }
    });

    // Form submission handling
    const productForms = document.querySelectorAll('product-form');
    productForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        this.handleFormSubmission(e, form);
      });
    });

    // Window resize - recalculate sticky positions
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.recalculateStickyPositions();
      }, 100);
    });
  }

  closeAllDrawers() {
    const drawers = document.querySelectorAll('.options-drawer, .stack-drawer');
    drawers.forEach(drawer => {
      drawer.classList.remove('open');
    });
    document.body.style.overflow = '';
  }

  handleFormSubmission(event, form) {
    // Add loading state
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.classList.add('loading');

      const spinner = submitButton.querySelector('.loading-overlay__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    }

    // Handle stack selections
    const stackSelections = document.getElementById('stack-selections');
    if (stackSelections && stackSelections.value) {
      // Stack selections are already added as properties
    }
  }

  recalculateStickyPositions() {
    // Force recalculation of sticky elements
    const stickyElements = document.querySelectorAll('.pdp-master__info-sticky, .pdp-master__form-sticky');

    stickyElements.forEach(element => {
      element.style.position = 'static';
      // Force reflow
      element.offsetHeight;
      element.style.position = 'sticky';
    });
  }

  // Utility method for theme editor
  onSectionLoad() {
    this.init();
  }

  onSectionUnload() {
    // Cleanup if needed
    this.closeAllDrawers();
  }
}

// Initialize PDP Master
document.addEventListener('DOMContentLoaded', () => {
  window.pdpMaster = new PDPMaster();
});

// Theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.pdp-master')) {
      window.pdpMaster = new PDPMaster();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (window.pdpMaster && event.target.querySelector('.pdp-master')) {
      window.pdpMaster.onSectionUnload();
    }
  });
}